import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { devError, devWarn } from '../lib/logger';
import { downloadPdfWithCache } from '../lib/pdf';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, BookOpen, Trash2, FileText, ExternalLink, Eye, RefreshCw, AlertCircle, Loader2, Zap, CheckCircle2, Copy, RotateCw, Search, X } from 'lucide-react';
import CatalogPreviewModal from '../components/CatalogPreviewModal';
import ShareCatalogMenu from '../components/ShareCatalogMenu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function CatalogsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMaster = user?.role === 'super_admin';
  const [catalogs, setCatalogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewCatalog, setPreviewCatalog] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState({});
  const [prewarmBusy, setPrewarmBusy] = useState({});
  const [regenBusy, setRegenBusy] = useState({});
  const [duplicateTarget, setDuplicateTarget] = useState(null); // { id, title, subtitle, client_name }
  const [duplicating, setDuplicating] = useState(false);
  const [search, setSearch] = useState('');

  // Case-insensitive client-side filter across title, subtitle, status,
  // language, currency code, and section names. Empty query passes through.
  const filteredCatalogs = useMemo(() => {
    const list = catalogs || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(c => {
      const parts = [
        c.title, c.subtitle, c.status, c.language,
        c.currency?.code, c.currency?.symbol,
        ...((c.sections || []).map(s => s.name)),
      ].filter(Boolean).map(String).join(' ').toLowerCase();
      return parts.includes(q);
    });
  }, [catalogs, search]);

  const fetchCatalogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/catalogs', { timeout: 30000 });
      setCatalogs(Array.isArray(data) ? data : []);
    } catch (err) {
      devError('Catalog list load failed', err);
      setError(formatApiError(err.response?.data?.detail) || err.message || 'Failed to load catalogs');
      setCatalogs(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCatalogs(); }, [fetchCatalogs]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete catalog "${title}"?`)) return;
    try {
      await api.delete(`/catalogs/${id}`);
      toast.success('Catalog deleted');
      fetchCatalogs();
    } catch (err) { devError('Delete catalog failed', err); toast.error(formatApiError(err.response?.data?.detail) || 'Failed to delete'); }
  };

  const downloadPdf = async (id, title) => {
    setPdfBusy(b => ({ ...b, [id]: true }));
    // Smart path: if PDF isn't cached yet, kick off background generation and poll
    // to completion — then serve the (now cached) PDF. This avoids the user clicking
    // "Export PDF" on a cold catalog and waiting through a Cloudflare-killed request.
    try {
      const { data: status } = await api.get(`/catalogs/${id}/pdf-status`);
      if (!status.pdf_ready) {
        const toastId = toast.loading('Generating PDF (large catalogs can take 30-60s)…');
        try { await api.post(`/catalogs/${id}/prewarm`); } catch (e) { devWarn('Prewarm trigger failed', e); }
        // Poll up to 180s in 4s increments — accommodates very large catalogs (200+ products).
        let waited = 0;
        const ready = await new Promise((resolve) => {
          const tick = async () => {
            try {
              const { data: s } = await api.get(`/catalogs/${id}/pdf-status`);
              if (s.pdf_ready) return resolve(true);
            } catch (e) { devWarn('Status poll failed', e); }
            waited += 4;
            if (waited >= 180) return resolve(false);
            setTimeout(tick, 4000);
          };
          setTimeout(tick, 3000);
        });
        if (!ready) {
          toast.error('PDF is still generating. Please try again in a minute.', { id: toastId });
          setPdfBusy(b => { const n = { ...b }; delete n[id]; return n; });
          return;
        }
        toast.dismiss(toastId);
      }
    } catch (e) { devWarn('PDF readiness check failed; attempting direct download', e); }
    await downloadPdfWithCache(`/catalogs/${id}/pdf`, title);
    setPdfBusy(b => { const n = { ...b }; delete n[id]; return n; });
    // refresh ready state once after a download (turns the badge "PDF Ready" if it just got cached)
    setTimeout(fetchCatalogs, 500);
  };

  const prewarmPdf = async (id) => {
    if (prewarmBusy[id]) return;
    setPrewarmBusy(b => ({ ...b, [id]: true }));
    const toastId = toast.loading('Preparing PDF in background…');
    try {
      const { data } = await api.post(`/catalogs/${id}/prewarm`);
      if (data.status === 'already_cached') {
        toast.success('PDF already ready', { id: toastId });
        setPrewarmBusy(b => { const n = { ...b }; delete n[id]; return n; });
        return;
      }
      // Poll status every 3s up to 90s
      let waited = 0;
      const poll = async () => {
        try {
          const { data: s } = await api.get(`/catalogs/${id}/pdf-status`);
          if (s.pdf_ready) {
            toast.success('PDF ready for instant download', { id: toastId });
            setPrewarmBusy(b => { const n = { ...b }; delete n[id]; return n; });
            fetchCatalogs();
            return;
          }
        } catch (e) { devWarn('Prewarm poll failed', e); }
        waited += 3;
        if (waited >= 180) {
          toast.error('PDF still preparing — refresh in a moment', { id: toastId });
          setPrewarmBusy(b => { const n = { ...b }; delete n[id]; return n; });
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (err) {
      devError('Prewarm failed', err);
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to start PDF preparation', { id: toastId });
      setPrewarmBusy(b => { const n = { ...b }; delete n[id]; return n; });
    }
  };

  const openDuplicateDialog = (catalog) => {
    setDuplicateTarget({
      id: catalog.id,
      original_title: catalog.title,
      title: catalog.title ? `${catalog.title} (Copy)` : '',
      subtitle: catalog.subtitle || '',
      client_name: catalog.back_cover?.name || '',
    });
  };

  const handleDuplicate = async () => {
    if (!duplicateTarget) return;
    const newTitle = duplicateTarget.title.trim();
    if (!newTitle) { toast.error('New catalog title is required'); return; }
    setDuplicating(true);
    try {
      const { data } = await api.post(`/catalogs/${duplicateTarget.id}/duplicate`, {
        title: newTitle,
        subtitle: duplicateTarget.subtitle || null,
        client_name: duplicateTarget.client_name || null,
      });
      toast.success('Catalog duplicated');
      setDuplicateTarget(null);
      navigate(`/catalogs/${data.id}/edit`);
    } catch (err) {
      devError('Duplicate catalog failed', err);
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to duplicate catalog');
    }
    setDuplicating(false);
  };

  const handleRegenerate = async (id, title) => {
    if (!window.confirm(`Force-regenerate PDF for "${title}"?\n\nThis clears the cached PDF and re-renders from current images. Use this when the cached PDF shows missing or broken images.`)) return;
    setRegenBusy(b => ({ ...b, [id]: true }));
    const toastId = toast.loading('Clearing cached PDF and regenerating…');
    try {
      const { data } = await api.post(`/catalogs/${id}/regenerate-pdf`);
      toast.success(`Cleared ${data.cleared_variants ?? 0} cached variant${(data.cleared_variants === 1) ? '' : 's'}. Generating fresh PDF…`, { id: toastId });
      // Poll until the new PDF is ready
      let waited = 0;
      const poll = async () => {
        try {
          const { data: s } = await api.get(`/catalogs/${id}/pdf-status`);
          if (s.pdf_ready) {
            toast.success('Fresh PDF ready', { id: toastId });
            setRegenBusy(b => { const n = { ...b }; delete n[id]; return n; });
            fetchCatalogs();
            return;
          }
        } catch (e) { devWarn('Regenerate poll failed', e); }
        waited += 3;
        if (waited >= 180) {
          toast.error('Regeneration still running — refresh in a minute', { id: toastId });
          setRegenBusy(b => { const n = { ...b }; delete n[id]; return n; });
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (err) {
      devError('Regenerate failed', err);
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to regenerate PDF', { id: toastId });
      setRegenBusy(b => { const n = { ...b }; delete n[id]; return n; });
    }
  };

  const openPreview = async (catalogId) => {
    try {
      const { data } = await api.get(`/catalogs/${catalogId}`);
      setPreviewCatalog(data);
      setPreviewOpen(true);
    } catch (err) { devError('Preview load failed', err); toast.error('Failed to load catalog preview'); }
  };

  const statusColor = (status) => {
    if (status === 'final') return 'bg-[#2D6A4F] text-white';
    if (status === 'archived') return 'bg-[#E5E0D8] text-[#5C5C5C]';
    return 'bg-[#F2E8D5] text-[#C5A059]';
  };

  const totalCatalogs = catalogs?.length ?? 0;
  const shownCount = filteredCatalogs.length;
  const isSearching = search.trim().length > 0;

  return (
    <div data-testid="catalogs-page" className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#1A1A1A] tracking-tight">Catalogs</h1>
          <p className="text-sm text-[#5C5C5C] mt-1" data-testid="catalogs-count">
            {loading
              ? 'Loading…'
              : error
                ? 'Could not load catalogs'
                : isSearching
                  ? `${shownCount} of ${totalCatalogs} catalog${totalCatalogs === 1 ? '' : 's'} found`
                  : `${totalCatalogs} catalog${totalCatalogs === 1 ? '' : 's'} created`}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={fetchCatalogs} variant="outline" disabled={loading} className="rounded-sm border-[#E5E0D8]" data-testid="refresh-catalogs-btn">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button onClick={() => navigate('/catalogs/new')} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="create-catalog-btn">
            <Plus size={16} className="mr-2" />New Catalog
          </Button>
        </div>
      </div>

      {/* Search bar — case-insensitive live filter across title, subtitle,
          status, language, currency, and section names. */}
      {!loading && !error && totalCatalogs > 0 && (
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8a8a] pointer-events-none" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search catalogs by title, status, language, currency…"
              className="pl-9 pr-9 rounded-sm border-[#E5E0D8] bg-white"
              data-testid="catalogs-search-input"
              aria-label="Search catalogs"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#8a8a8a] hover:text-[#1A1A1A]"
                data-testid="catalogs-search-clear"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="catalogs-loading">
          <div className="spinner-gold" />
          <p className="text-xs text-[#5C5C5C]">Loading catalogs…</p>
        </div>
      ) : error ? (
        <div className="text-center py-16 bg-white border border-[#E5E0D8] rounded-sm" data-testid="catalogs-error">
          <AlertCircle size={40} className="mx-auto text-[#9E2A2B] mb-4" />
          <p className="text-[#1A1A1A] font-medium mb-2">Could not load catalogs</p>
          <p className="text-xs text-[#5C5C5C] mb-4 max-w-md mx-auto">{error}</p>
          <Button onClick={fetchCatalogs} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="retry-catalogs-btn">
            <RefreshCw size={14} className="mr-2" />Retry
          </Button>
        </div>
      ) : totalCatalogs === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E0D8] rounded-sm" data-testid="empty-catalogs">
          <BookOpen size={40} className="mx-auto text-[#E5E0D8] mb-4" />
          <p className="text-[#5C5C5C] mb-4">No catalogs created yet</p>
          <Button onClick={() => navigate('/catalogs/new')} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm">Create First Catalog</Button>
        </div>
      ) : shownCount === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E0D8] rounded-sm" data-testid="catalogs-no-search-results">
          <Search size={36} className="mx-auto text-[#E5E0D8] mb-4" />
          <p className="text-[#1A1A1A] font-medium mb-2">No catalogs found</p>
          <p className="text-xs text-[#5C5C5C] mb-4">No catalog matches &ldquo;{search}&rdquo;. Try a different keyword.</p>
          <Button onClick={() => setSearch('')} variant="outline" className="rounded-sm border-[#E5E0D8]" data-testid="clear-search-empty-btn">Clear search</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCatalogs.map(c => (
            <div key={c.id} className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`catalog-card-${c.id}`}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-serif text-lg text-[#1A1A1A] leading-tight">{c.title}</h3>
                  <Badge className={`${statusColor(c.status)} text-[10px] ml-2 shrink-0`}>{c.status}</Badge>
                </div>
                {c.subtitle && <p className="text-sm text-[#5C5C5C] mb-3">{c.subtitle}</p>}
                <div className="flex gap-4 text-xs text-[#5C5C5C] mb-3 items-center">
                  <span>{c.section_count ?? c.sections?.length ?? 0} sections</span>
                  <span>{c.total_products || 0} products</span>
                  {c.pdf_ready ? (
                    <Badge className="bg-[#E8F5E9] text-[#2D6A4F] text-[10px] gap-1" data-testid={`pdf-ready-${c.id}`}>
                      <CheckCircle2 size={10} />PDF Ready
                    </Badge>
                  ) : (
                    <Badge className="bg-[#F5F2EB] text-[#5C5C5C] text-[10px]" data-testid={`pdf-not-prepared-${c.id}`}>Not Prepared</Badge>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => openPreview(c.id)} className="rounded-sm border-[#E5E0D8] text-xs" data-testid={`preview-catalog-${c.id}`}><Eye size={12} className="mr-1" />Preview</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/catalogs/${c.id}/edit`)} className="rounded-sm border-[#E5E0D8] text-xs" data-testid={`edit-catalog-${c.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" disabled={!!pdfBusy[c.id]} onClick={() => downloadPdf(c.id, c.title)} className="rounded-sm border-[#E5E0D8] text-xs" data-testid={`pdf-catalog-${c.id}`}>
                    {pdfBusy[c.id] ? <Loader2 size={12} className="mr-1 animate-spin" /> : <FileText size={12} className="mr-1" />}
                    PDF
                  </Button>
                  {isMaster && !c.pdf_ready && (
                    <Button size="sm" variant="outline" disabled={!!prewarmBusy[c.id]} onClick={() => prewarmPdf(c.id)} className="rounded-sm border-[#C5A059] text-xs text-[#C5A059] hover:bg-[#F2E8D5]" data-testid={`prewarm-catalog-${c.id}`}>
                      {prewarmBusy[c.id] ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Zap size={12} className="mr-1" />}
                      Prepare PDF
                    </Button>
                  )}
                  {isMaster && c.pdf_ready && (
                    <Button size="sm" variant="outline" disabled={!!regenBusy[c.id]} onClick={() => handleRegenerate(c.id, c.title)} className="rounded-sm border-[#E5E0D8] text-xs text-[#5C5C5C] hover:bg-[#F5F2EB]" data-testid={`regenerate-catalog-${c.id}`} title="Regenerate PDF — use when cached PDF has missing or broken images">
                      {regenBusy[c.id] ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RotateCw size={12} className="mr-1" />}
                      Regenerate
                    </Button>
                  )}
                  <ShareCatalogMenu catalog={c} onDownloadPdf={() => downloadPdf(c.id, c.title)} pdfBusy={!!pdfBusy[c.id]} />
                  <Button size="sm" variant="outline" onClick={() => window.open(`/shared-mobile/${c.share_token}`, '_blank')} className="rounded-sm border-[#E5E0D8] text-xs" data-testid={`open-mobile-quick-${c.id}`}><ExternalLink size={12} className="mr-1" />Open</Button>
                  <Button size="sm" variant="outline" onClick={() => openDuplicateDialog(c)} className="rounded-sm border-[#E5E0D8] text-xs" data-testid={`duplicate-catalog-${c.id}`}><Copy size={12} className="mr-1" />Duplicate</Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(c.id, c.title)} className="rounded-sm border-[#E5E0D8] text-xs text-[#9E2A2B] hover:bg-red-50"><Trash2 size={12} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <CatalogPreviewModal
        catalog={previewCatalog}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onDownloadPdf={previewCatalog ? () => downloadPdf(previewCatalog.id, previewCatalog.title) : null}
      />

      <Dialog open={!!duplicateTarget} onOpenChange={(open) => { if (!open) setDuplicateTarget(null); }}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8]" data-testid="duplicate-catalog-dialog">
          <DialogHeader><DialogTitle className="font-serif text-lg">Save as New Catalog</DialogTitle></DialogHeader>
          {duplicateTarget && (
            <>
              <p className="text-xs text-[#5C5C5C] -mt-2">Duplicating <span className="font-medium text-[#1A1A1A]">{duplicateTarget.original_title}</span>. All sections, products, pricing rules and branding will be cloned. The original is untouched.</p>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">New Title *</Label>
                  <Input data-testid="duplicate-title-input" autoFocus value={duplicateTarget.title} onChange={e => setDuplicateTarget(p => ({ ...p, title: e.target.value }))} className="rounded-sm border-[#E5E0D8]" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Subtitle</Label>
                  <Input data-testid="duplicate-subtitle-input" value={duplicateTarget.subtitle} onChange={e => setDuplicateTarget(p => ({ ...p, subtitle: e.target.value }))} className="rounded-sm border-[#E5E0D8]" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Distributor / Client Name</Label>
                  <Input data-testid="duplicate-client-input" value={duplicateTarget.client_name} onChange={e => setDuplicateTarget(p => ({ ...p, client_name: e.target.value }))} placeholder="Optional — appears on the back cover" className="rounded-sm border-[#E5E0D8]" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" disabled={duplicating} onClick={() => setDuplicateTarget(null)} className="rounded-sm border-[#E5E0D8]" data-testid="duplicate-cancel-btn">Cancel</Button>
                <Button onClick={handleDuplicate} disabled={duplicating} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="confirm-duplicate-btn">
                  {duplicating ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Copy size={14} className="mr-2" />}
                  {duplicating ? 'Duplicating…' : 'Create Copy'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
